import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _excluded = ["children", "className", "dataTestId", "dimensionRef", "isAuto", "isOpen", "labelRef", "multi", "onClose", "onOpen", "placement", "style", "triggerOffset"];

var _templateObject;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}
/* eslint-disable indent */
// Running eslint fix causes the function flatChildren to break as it adds indentations where not needed


import React, { cloneElement, createRef, forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { KEY_CODES, getPositionStyles, getDynamicPosition, flatMap, removeObjectProperties } from "../../utilities";
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useWindowWidth } from "../../utilities";
import { MENU_PLACEMENT } from "../constants";
import { StyledMenuContainer } from "../elements";
import { useOutsideClickEventListener } from "../utilities";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var StyledItemContainer = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  padding: ", " 0;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.size.spacing.small.value;
});

var getScrollableParent = function getScrollableParent(node) {
  if (node === null || node === undefined) {
    return null;
  } else if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollableParent(node.parentNode);
  }
};

var Menu = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
      className = _ref2.className,
      dataTestId = _ref2.dataTestId,
      dimensionRef = _ref2.dimensionRef,
      isAuto = _ref2.isAuto,
      isOpen = _ref2.isOpen,
      labelRef = _ref2.labelRef,
      multi = _ref2.multi,
      onClose = _ref2.onClose,
      onOpen = _ref2.onOpen,
      placement = _ref2.placement,
      style = _ref2.style,
      triggerOffset = _ref2.triggerOffset,
      other = _objectWithoutProperties(_ref2, _excluded);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      targetNode = _useState2[0],
      setTargetNode = _useState2[1];

  var focusItem = useRef(null);

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      keyBoardFocus = _useState4[0],
      setKeyBoardFocus = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      openMenu = _useState6[0],
      setOpenMenu = _useState6[1];

  var menuRef = useRef(document.createElement('div'));
  var ARROW_DOWN = KEY_CODES.ARROW_DOWN,
      ARROW_UP = KEY_CODES.ARROW_UP,
      ENTER = KEY_CODES.ENTER,
      ESCAPE = KEY_CODES.ESCAPE,
      SPACE = KEY_CODES.SPACE,
      TAB = KEY_CODES.TAB;
  var menuContainerRef = useRef();
  var menuOpenStatus = useRef(null);
  var flatChildren = Array.isArray(children) ? flatMap(children, function (item) {
    return Array.isArray(item) ? flatMap(item, function (item) {
      return item;
    }) : item;
  }) : [children];
  var menuListRef = useRef(flatChildren.map(function () {
    return /*#__PURE__*/createRef();
  })); // Following hook adds div to document body and assign node to menu

  useEffect(function () {
    document.body.appendChild(menuRef.current);
    setTargetNode(menuRef.current);
    setOpenMenu(isOpen);
  }, [isOpen]); // This section closes the Menu on screen resize

  var scanWindow = useWindowWidth(5);
  useLayoutEffect(function () {
    if (openMenu) {
      setOpenMenu(false);
    }
  }, [scanWindow]);

  var handleMenu = function handleMenu() {
    setOpenMenu(function (openMenu) {
      return !openMenu;
    });
  };

  var handleClose = function handleClose() {
    menuOpenStatus.current = false;
    setOpenMenu(false);
    setKeyBoardFocus(0);
    labelRef.current.focus();
  };

  var setSelectedFocusStyles = function setSelectedFocusStyles(ref) {
    if (!multi) {
      handleClose();
    }

    focusItem.current = ref;
  }; //  Following hook adds click and keydown event handlers to Label element


  useEffect(function () {
    var labelElm = labelRef.current;

    if (labelElm) {
      labelElm.addEventListener('click', handleMenu);
      labelElm.addEventListener('keydown', handleLabelKeyDown);
    } else {
      console.error('Missing Label ref, labelRef should match label component ref ');
    }

    return function () {
      if (labelElm) {
        labelElm.removeEventListener('click', handleMenu);
        labelElm.removeEventListener('keydown', handleLabelKeyDown);
      }
    };
  }, [labelRef]);
  var handleLabelKeyDown = useCallback(function (event) {
    if ([ARROW_UP, ARROW_DOWN].includes(event.keyCode)) {
      event.preventDefault();
      setOpenMenu(true);
      setKeyBoardFocus(event.keyCode);
      var focusedItem = focusItem.current;

      if (focusedItem) {
        focusedItem.current.focus();
      } else {
        var listItemRef = menuListRef.current;
        var itemsLength = listItemRef.length - 1;

        if (event.keyCode === ARROW_DOWN) {
          listItemRef[0].current.focus();
        } else if (event.keyCode === ARROW_UP) {
          listItemRef[itemsLength].current.focus();
        }
      }
    }

    if ([ESCAPE, TAB].includes(event.keyCode)) {
      handleClose();
    }

    if ([ENTER, SPACE].includes(event.keyCode)) {
      event.preventDefault();
      handleMenu();
    }
  });
  var initialRender = true;
  useEffect(function () {
    if (onOpen && openMenu) {
      onOpen();
    }

    if (initialRender && onClose && !openMenu) {
      initialRender = false;
      onClose();
    }

    if (openMenu) {
      menuOpenStatus.current = true;
    }

    var scrollParent = getScrollableParent(labelRef.current);

    if (labelRef.current) {
      document.addEventListener('scroll', function () {
        if (menuOpenStatus.current) {
          handleClose();
        }
      }); // eslint-disable-next-line mdx/no-unused-expressions

      !!scrollParent && scrollParent.addEventListener('scroll', function () {
        if (menuOpenStatus.current) {
          handleClose();
        }
      });
    }

    var focusedItem = focusItem.current;

    if (focusedItem) {
      focusedItem.current.focus();
    } else {
      var listItemRef = menuListRef.current;
      var itemsLength = listItemRef.length - 1;

      if (keyBoardFocus === ARROW_DOWN) {
        listItemRef[0].current.focus();
      } else if (keyBoardFocus === ARROW_UP) {
        listItemRef[itemsLength].current.focus();
      }
    }

    return function () {
      if (labelRef.current) {
        document.removeEventListener('scroll', handleClose); // eslint-disable-next-line mdx/no-unused-expressions

        !!scrollParent && scrollParent.removeEventListener('scroll', handleClose);
      }
    };
  }, [openMenu]);
  var listItemKeyDown = useCallback(function (event) {
    var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var eventTarget = target || event.target;

    if (event.keyCode === ARROW_DOWN) {
      event.preventDefault();

      if (eventTarget && eventTarget.nextSibling) {
        if (eventTarget.nextSibling.getAttribute('disabled') !== null) {
          return listItemKeyDown(event, eventTarget.nextSibling);
        }

        eventTarget.nextSibling.focus();
      } else {
        menuListRef.current[0].current.focus();
      }
    }

    if (event.keyCode === ARROW_UP) {
      event.preventDefault();

      if (eventTarget && eventTarget.previousElementSibling) {
        if (eventTarget.previousElementSibling.getAttribute('disabled') !== null) {
          return listItemKeyDown(event, eventTarget.previousElementSibling);
        }

        eventTarget.previousElementSibling.focus();
      } else {
        var listLength = menuListRef.current.length;
        menuListRef.current[listLength - 1].current.focus();
      }
    }

    if ([ESCAPE, TAB].includes(event.keyCode)) {
      handleClose();
    }
  });
  useEffect(function () {
    // eslint-disable-next-line array-callback-return
    menuListRef.current.map(function (el) {
      if (el.current) {
        el.current.addEventListener('keydown', listItemKeyDown);
      }
    });
    return function () {
      // eslint-disable-next-line array-callback-return
      menuListRef.current.map(function (el) {
        if (el.current) {
          el.current.removeEventListener('keydown', listItemKeyDown);
        }
      });
    };
  }, [listItemKeyDown, openMenu]);
  useOutsideClickEventListener(menuRef, labelRef, handleClose, openMenu);
  var isKeyboardFocused = keyBoardFocus !== 0;
  var position = isAuto ? getDynamicPosition(placement, menuContainerRef, dimensionRef || labelRef, openMenu, triggerOffset) : placement;
  var styles = getPositionStyles(position, triggerOffset, dimensionRef || labelRef, openMenu, menuContainerRef);
  var isStyleNull = isNaN(styles.top) && isNaN(styles.left);
  var finalStyles = isStyleNull ? removeObjectProperties(styles, 'top', 'left') : styles;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: openMenu && /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(StyledMenuContainer, _objectSpread(_objectSpread({
      style: style ? _objectSpread(_objectSpread({}, style), finalStyles) : finalStyles,
      ref: menuContainerRef,
      "data-testid": dataTestId,
      className: className
    }, other), {}, {
      children: /*#__PURE__*/_jsx(StyledItemContainer, {
        children: flatChildren.length > 0 && flatChildren.map(function (child, ind) {
          return /*#__PURE__*/cloneElement(child, {
            ref: menuListRef.current[ind],
            key: ind.toString(),
            tabIndex: 0,
            isKeyboardFocused: isKeyboardFocused,
            showCheckbox: !!multi,
            onSelect: setSelectedFocusStyles
          });
        })
      })
    })), targetNode)
  });
});
Menu.propTypes = {
  /** the Ref of the wrapper element  */
  labelRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }.isRequired),

  /** Menu items to populate menu */
  children: PropTypes.array.isRequired,

  /** Adds new class to Menu */
  className: PropTypes.string,

  /** the Ref of the element relative to which the menu will be positioned */
  dimensionRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),

  /** Call back function will trigger after opening Menu */
  onOpen: PropTypes.func,

  /** Call back function will trigger after closing Menu */
  onClose: PropTypes.func,

  /** If true, switch automatically to a placement that is more visible on the screen */
  isAuto: PropTypes.bool,

  /** Flag to open/close menu */
  isOpen: PropTypes.bool,

  /** Preferred placement of the menu */
  placement: PropTypes.oneOf(Object.values(MENU_PLACEMENT)),

  /** Custom inline style applied for the menu */
  style: PropTypes.shape({}),

  /** Parameter defining space between the target component and popup. */
  triggerOffset: PropTypes.number,

  /** Flag to enable multi selection */
  multi: PropTypes.bool,

  /** Id value used for testing */
  dataTestId: PropTypes.string
};
Menu.defaultProps = {
  className: '',
  dimensionRef: undefined,
  isAuto: true,
  placement: MENU_PLACEMENT.BOTTOM_START,
  style: {},
  triggerOffset: 4,
  dataTestId: '',
  multi: false,
  isOpen: false,
  onClose: undefined,
  onOpen: undefined
};
export { Menu };